<template>
  <form
    method="post"
    @submit.prevent="submit"
  >
    <v-modal
      @hide-modal="$emit('hide-modal')"
      size="md"
      :fit-height-to-screen="true"
    >
      <template v-slot:title>
        <h4>
          <i class="fa fa-filter mr-2"></i>
          {{ $t('phrase.all_filters') }}
        </h4>
      </template>
      <template v-slot:body>
        <div class="card card-body bg-light p-3 mb-2">
          <div class="mb-2">
            <h6 class="m-0">{{ $t('word.gender') }}</h6>
            <div class="d-flex">
              <label class="mr-2">
                <input
                  type="checkbox"
                  id="filter-gender-f"
                  v-model="filters.gender.f"
                />
                {{ $t('word.female') }}
              </label>
              <label class="mr-2">
                <input
                  type="checkbox"
                  id="filter-gender-m"
                  v-model="filters.gender.m"
                >
                {{ $t('word.male') }}
              </label>
              <label class="mr-2">
                <input
                  type="checkbox"
                  id="filter-gender-o"
                  v-model="filters.gender.o"
                >
                {{ $t('word.other') }}
              </label>
            </div>
          </div>

          <div class="mb-2">
            <h6 class="m-0">{{ $t('word.age') }}</h6>
            <div class="d-flex align-items-center flex-nowrap mr-2">
              <input
                type="number"
                id="filter-age-min"
                class="form-control text-center"
                :placeholder="$t('phrase.age_from')"
                v-model="filters.age.min"
                min="0"
                max="100"
              />
              <span class="text-bold ml-2 mr-2">-</span>
              <input
                type="number"
                id="filter-age-max"
                class="form-control text-center"
                :placeholder="$t('phrase.age_til')"
                v-model="filters.age.max"
                min="0"
                max="100"
              />
            </div>
          </div>

          <div>
            <h6 class="m-0">{{ $t('word.region') }}</h6>
            <div>
              <v-check-boxes
                :choices="provinces"
                v-model="filters.provinces"
                id="province-checkboxes"
                :columns="3"
              />
            </div>
          </div>
        </div>
        <hr/>
        <div class="alert alert-info text-sm-left mb-2">
          <i class="fa fa-info-circle"></i>
          {{ $t('sentence.no_benchmarking_extra_filters') }}
        </div>
        <div
          v-for="researchItem in filterableResearchItems"
          :key="researchItem.id"
          class="card card-body p-3 bg-light mb-2"
        >
          <div class="mb-2">
            <h6 class="m-0">
              <span class="badge badge-pill badge-dark mr-2">
                {{ researchItem.order + 1 }}
              </span>
              <span v-html="formattedItemText(researchItem.title)"/>
            </h6>
          </div>
          <div v-if="isChoice(researchItem.item.render_type)">
            <v-check-boxes
              :choices="getResearchItemOptions(researchItem)"
              v-model="researchItemFilters[researchItem.id]"
              :id="`filter-${researchItem.id}`"
            />
          </div>
          <div
            v-else-if="isNumeric(researchItem.item.render_type)"
            class="d-flex justify-content-between"
          >
            <div class="badge bg-cr-red-black text-white">
              {{ researchItemFilters[researchItem.id][0] }}
            </div>
            <div class="w-100 pr-3 pl-3">
              <v-slider
                :id="`filter-${researchItem.id}`"
                v-model="researchItemFilters[researchItem.id]"
                :dot-options="dotOptions"
                :max="10"
                :min="1"
              />
            </div>
            <div class="badge bg-cr-red-black text-white">
              {{ researchItemFilters[researchItem.id][1] }}
            </div>
          </div>
          <div v-if="researchItem.condition_groups.length > 0">
            <span
              v-for="group in researchItem.condition_groups"
              :key="group.id"
              class="badge badge-pill badge-info mr-2"
            >{{ group.description }}</span>
          </div>
        </div>
      </template>
      <template v-slot:footer>
        <v-button
          style-type="dark"
          icon="filter"
          text="word.filter"
          type="submit"
        />
      </template>
    </v-modal>
  </form>
</template>

<script>
import Button from '@/common/components/Button.vue';
import CheckBoxes from '@/common/components/inputs/CheckBoxes.vue';
import Modal from '@/common/components/Modal.vue';
import { getProvinces } from '@/organisation/services/researches';
import VueSlider from 'vue-slider-component';
import {
  formattedItemText,
  isChoice,
  isNumeric,
} from '@/common/services/items';

export default {
  name: 'ResearchFilterModal',
  components: {
    'v-slider': VueSlider,
    'v-button': Button,
    'v-check-boxes': CheckBoxes,
    'v-modal': Modal,
  },
  props: {
    countryCode: {
      type: String,
      required: true,
    },
    filters: {
      type: Object,
      required: true,
    },
    filterableResearchItems: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      researchItemFilters: {},
    };
  },
  computed: {
    dotOptions() {
      return [
        {
          min: 1,
          max: 10,
        },
        {
          min: 1,
          max: 10,
        },
      ];
    },
    provinces() {
      return getProvinces(this.countryCode);
    },
  },
  methods: {
    formattedItemText,
    isNumeric,
    isChoice,
    getResearchItemOptions(researchItem) {
      return researchItem.item.options.map(
        (option) => ({ key: option.value, value: option.short_value || option.value }),
      );
    },
    getDefaultFilterValue(researchItem) {
      if (isNumeric(researchItem.item.render_type)) {
        if (!this.filters.researchItemFilters) {
          return [1, 10];
        }
        return this.filters.researchItemFilters[researchItem.id] || [1, 10];
      }
      if (isChoice(researchItem.item.render_type)) {
        if (!this.filters.researchItemFilters) {
          return [];
        }
        return this.filters.researchItemFilters[researchItem.id] || [];
      }
      return [];
    },
    shouldUseFilter(researchItem) {
      if (isNumeric(researchItem.item.render_type)) {
        if (
          this.researchItemFilters[researchItem.id][0] !== 1
          || this.researchItemFilters[researchItem.id][1] !== 10
        ) {
          return true;
        }
      } else if (isChoice(researchItem.item.render_type)) {
        if (this.researchItemFilters[researchItem.id].length > 0) {
          return true;
        }
      }
      return false;
    },
    submit() {
      const researchItemFilters = {};
      this.filterableResearchItems
        .filter(this.shouldUseFilter)
        .forEach((researchItem) => {
          researchItemFilters[researchItem.id] = this.researchItemFilters[researchItem.id];
        });
      if (Object.keys(researchItemFilters).length !== 0) {
        this.filters.researchItemFilters = researchItemFilters;
      }
      this.$emit('submit');
    },
  },
  created() {
    this.filterableResearchItems.forEach((researchItem) => {
      this.$set(
        this.researchItemFilters,
        researchItem.id,
        this.getDefaultFilterValue(researchItem),
      );
    });
  },
};
</script>

<style scoped>
.filter-modal {
  height: 100vh;
}
</style>
